/**
 * SEO Utilities for consistent meta tag management
 */

import type { CognitiveBias } from '../types';

export const SITE_URL = 'https://www.cognitivebiaslab.com';
export const DEFAULT_TITLE = 'Cognitive Bias Lab | Interactive Learning Platform';
export const DEFAULT_DESCRIPTION = 'Master cognitive biases through interactive learning. Free educational platform with tests, flashcards, and real-world examples to improve your decision-making skills.';

// Add debug logging for SEO functions
const logSeoDebug = (functionName: string, input: any, output: string) => {
  if (typeof window !== 'undefined' && (window as any).__PRERENDER_INJECTED) {
    console.log(`[SEO Debug] ${functionName}:`, {
      input: typeof input === 'object' ? input.name || input : input,
      output: output.substring(0, 100) + (output.length > 100 ? '...' : '')
    });
  }
};

/**
 * Generate consistent meta description for a bias
 */
export const generateBiasDescription = (bias: CognitiveBias): string => {
  // If there's a custom meta description, use it
  if (bias.metaDescription) {
    logSeoDebug('generateBiasDescription', bias, bias.metaDescription);
    return bias.metaDescription;
  }
  
  // Create a more unique and descriptive meta description
  const categoryMap: Record<string, string> = {
    'Memory': 'memory and recall',
    'Decision-Making': 'decision-making processes',
    'Social': 'social interactions and perceptions',
    'Perceptual': 'perception and interpretation of information',
    'Reasoning': 'logical reasoning and problem-solving'
  };
  
  const categoryDescription = categoryMap[bias.category] || bias.category.toLowerCase();
  
  // Create a unique description that's more detailed
  let description = `Learn about ${bias.name}, a cognitive bias that affects ${categoryDescription}. `;
  
  // Add more specific details based on the bias description
  if (bias.short_description) {
    // Clean up the description and make it more SEO-friendly
    const cleanDescription = bias.short_description
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 120);
      
    description += `This bias ${cleanDescription.toLowerCase()}... `;
  }
  
  // Add a call to action
  description += `Discover examples, prevention strategies, and how to overcome ${bias.name.toLowerCase()}.`;
  
  logSeoDebug('generateBiasDescription', bias, description);
  return description;
};

/**
 * Generate consistent page title for a bias
 * This no longer needs "| Cognitive Bias Lab" suffix as that's handled by titleTemplate in App.tsx
 */
export const generateBiasTitle = (bias: CognitiveBias): string => {
  // If there's a custom title, use it
  if (bias.metaTitle) {
    logSeoDebug('generateBiasTitle', bias, bias.metaTitle);
    return bias.metaTitle;
  }
  
  // Create a more unique and descriptive title
  // Create simpler, more direct title as requested
  const title = `${bias.name} - Definition, Examples, and How to Overcome It`;
  logSeoDebug('generateBiasTitle', bias, title);
  return title;
};

/**
 * Generate consistent keywords for a bias
 */
export const generateBiasKeywords = (bias: CognitiveBias): string => {
  // Create a unique set of keywords by combining bias-specific keywords with general ones
  const keywords = [
    ...(bias.keywords || []),
    bias.name.toLowerCase(),
    bias.category.toLowerCase(),
    'cognitive bias',
    'decision making',
    'psychology',
    'critical thinking',
    `${bias.category.toLowerCase()} bias`,
    'overcome bias',
    'recognize bias',
    'examples of ' + bias.name.toLowerCase(),
    'how to avoid ' + bias.name.toLowerCase()
  ].join(', ');
  
  logSeoDebug('generateBiasKeywords', bias, keywords);
  return keywords;
};

/**
 * Generate consistent structured data for a bias article
 */
export const generateBiasStructuredData = (bias: CognitiveBias, biasId: string): string => {
  const articleDescription = generateBiasDescription(bias);
  const keywords = generateBiasKeywords(bias);

  // Build the structured data as a JS object for easier handling and correctness
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": bias.name,
    "description": articleDescription, // Use the longer description for the article itself
    "image": `${SITE_URL}/logo.webp`, // Consider updating this to a bias-specific image later if available
    "author": {
      "@type": "Organization",
      "name": "Cognitive Bias Lab"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Cognitive Bias Lab",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.webp`
      }
    },
    "url": `${SITE_URL}/bias/${biasId}`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/bias/${biasId}`
    },
    "keywords": keywords,
    // Add the DefinedTerm schema using the 'about' property
    "about": {
      "@type": "DefinedTerm",
      "name": bias.name,
      "description": bias.short_description // Use the short description specifically for the definition
    }
  };

  // Convert the object to a JSON string
  const structuredData = JSON.stringify(schema, null, 2); // Using null, 2 for pretty printing, can remove for production

  logSeoDebug('generateBiasStructuredData', bias, structuredData);
  return structuredData;
}; 